import React, { useEffect, useState } from "react";
import { Plus, Inbox, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import api from "../lib/api";
import AssetWatchlist from "./AssetWatchlist";
import SetupDetailView from "./SetupDetailView";
import { toast } from "sonner";
import { isAttractive } from "../lib/proximity";

export default function Dashboard({ setups, onAddSetup, onEditSetup, onRefresh }) {
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("ALLE");
  const [polling, setPolling] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [onlyAttractive, setOnlyAttractive] = useState(false);

  // Auto-select first setup on load (or after add)
  useEffect(() => {
    if (!selectedId && setups.length > 0) {
      setSelectedId(setups[0].id);
    }
    // If selected is gone, clear or pick first
    if (selectedId && !setups.find(s => s.id === selectedId)) {
      setSelectedId(setups.length > 0 ? setups[0].id : null);
    }
    // eslint-disable-next-line
  }, [setups]);

  const filteredSetups = setups.filter(s => {
    if (category !== "ALLE" && s.category !== category) return false;
    if (filter && !s.asset_name.toLowerCase().includes(filter.toLowerCase()) &&
        !s.symbol.toLowerCase().includes(filter.toLowerCase())) return false;
    if (onlyAttractive && !isAttractive(s)) return false;
    return true;
  });

  const attractiveCount = setups.filter(isAttractive).length;

  const handlePoll = async () => {
    setPolling(true);
    try {
      const r = await api.pollNow();
      toast.success(`${r.setups_polled} Setups geprüft. ${r.new_alerts} neue Alerts.`);
      onRefresh?.();
    } catch {
      toast.error("Polling fehlgeschlagen.");
    } finally {
      setPolling(false);
    }
  };

  const categories = ["ALLE", ...Array.from(new Set(setups.map(s => s.category)))];

  return (
    <div className="grid grid-cols-12 gap-0 min-h-[calc(100vh-3.5rem-2.25rem)]" data-testid="dashboard-page">
      {/* Watchlist column - schmaler (col-span-2) */}
      <aside className="col-span-12 lg:col-span-2 xl:col-span-2 border-r border-zinc-800 bg-zinc-950/40 max-h-[calc(100vh-5.75rem)] sticky top-[5.75rem] overflow-y-auto">
        <div className="p-3 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-display font-bold text-sm tracking-tight">Watchlist</div>
              <div className="text-[10px] text-zinc-500 font-mono-num">{filteredSetups.length} / {setups.length} Setups</div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handlePoll}
              disabled={polling || setups.length === 0}
              className="h-7 px-2 border-zinc-700 bg-transparent hover:bg-zinc-800 text-[10px]"
              data-testid="btn-poll-now"
              title="Preise aktualisieren"
            >
              <RefreshCw size={12} className={polling ? "animate-spin" : ""} />
            </Button>
          </div>
          <Input
            placeholder="Suche..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-zinc-900 border-zinc-800 h-8 text-xs"
            data-testid="search-setups"
          />
          <button
            onClick={() => setOnlyAttractive(!onlyAttractive)}
            data-testid="toggle-only-attractive"
            className={`mt-2 w-full px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm flex items-center justify-center gap-1.5 transition-colors border ${
              onlyAttractive
                ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800"
            }`}
            title="Zeige nur Setups mit ✅ oder Preis in/nahe Entry-Zone"
          >
            <Sparkles size={11} className={onlyAttractive ? "text-emerald-400" : ""} />
            Nur attraktiv ({attractiveCount})
          </button>
          <div className="flex flex-wrap gap-1 mt-2">
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`cat-filter-${c}`}
                className={`px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm ${
                  category === c ? "bg-white text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <AssetWatchlist
          setups={filteredSetups}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      </aside>

      {/* Main content - jetzt breiter (col-span-10) */}
      <main className="col-span-12 lg:col-span-10 xl:col-span-10 p-4 md:p-6 max-w-full overflow-x-hidden">
        {setups.length === 0 ? (
          <EmptyState onAdd={onAddSetup} />
        ) : selectedId ? (
          <SetupDetailView
            setupId={selectedId}
            onDeleted={() => { setSelectedId(null); onRefresh?.(); }}
            onChanged={() => onRefresh?.()}
            onEdit={onEditSetup}
            embedded
          />
        ) : (
          <div className="text-zinc-500 text-sm">Wähle ein Setup aus der Watchlist.</div>
        )}
      </main>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="border border-dashed border-zinc-800 p-12 text-center mx-auto max-w-2xl mt-8" data-testid="empty-state">
      <Inbox size={48} className="mx-auto text-zinc-700 mb-4" strokeWidth={1} />
      <h3 className="font-display font-bold text-2xl mb-2">Willkommen im Friedrich Allocation Hub</h3>
      <p className="text-zinc-500 text-sm mb-6 max-w-md mx-auto leading-relaxed">
        Füge dein erstes Setup hinzu — entweder per Friedrich-Report URL, durch Einfügen einer E-Mail
        oder manuell. Claude Sonnet 4.5 extrahiert Zonen, Targets und Stop-Loss automatisch.
      </p>
      <Button
        onClick={onAdd}
        className="bg-white text-black hover:bg-zinc-200 rounded-sm font-display font-bold uppercase tracking-wider"
        data-testid="btn-empty-add"
      >
        <Plus size={14} className="mr-1" /> Erstes Setup anlegen
      </Button>
    </div>
  );
}
